/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import {
  View,
  Stack,
  OverlayView,
  Header,
  Text,
  withTranslation,
  Layout,
  Select,
  Button,
  Badge,
  Skeleton,
  Icon,
  TextStyle,
  formatDate,
  formatToOptions,
  Currency,
  HelpLink,
  StyledContainer,
  Tooltip,
} from '@piwikpro/ui-components';
import moment from 'moment';
import { connect, ConnectedProps } from 'react-redux';
import { History } from 'history';
import classNameBind from 'classnames/bind';
import { TFunction } from 'react-i18next';
import { withInject, ConfigService, errorBoundary } from '@piwikpro/platform';
import { Organization, OrganizationState } from '@piwikpro/organization-crate';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import {
  Environments, initializePaddle, Paddle, PaddleEventData,
} from '@paddle/paddle-js';
import { BillingService } from '../../BillingService';
import { MarketingCratePaymentsState } from '../../redux';
import { MarketingService } from '../../MarketingService';
import styles from './ChangePlanOverlay.module.css';
import FeatureList from './components/FeatureList';
import { PlanCard } from './components/PlanCard';
import ChangeProcessingInfoModal, { ChangeProcessingModalVariant } from '../confirmation/ChangeProcessingInfoModal';
import { SubscriptionUpdateAction } from '../confirmation/ApplyNewSubscriptionConfirmation';

const bindClasses = classNameBind.bind(styles);
const EMPTY_STATE_TIER = {
  traffic: 600000,
  billing_cycle: 'year',
};
const EURO_SIGN = '€';

type BillingCycle = 'month' | 'year'
type Tier = {
  id: string
  traffic: number
  billing_cycle: BillingCycle
  price?: number
}

const findTierById = (tiers: Tier[] | null, tierId?: string): Tier | undefined =>
  tiers?.find(tier => tier.id === tierId);

const getAction = (
  newTier?: Tier,
  currentTier?: Tier,
): SubscriptionUpdateAction => {
  if (!newTier || !currentTier) return 'upgrade';

  const { billing_cycle: currentCycle, traffic: currentTraffic } = currentTier;
  const { billing_cycle: newCycle, traffic: newTraffic } = newTier;

  const isBillingCycleUpgrade = newCycle === 'year' && currentCycle === 'month';
  const isBillingCycleDowngrade = newCycle === 'month' && currentCycle === 'year';
  const isTierUpgrade = newTraffic > currentTraffic;
  const isTierDowngrade = newTraffic < currentTraffic;

  if (isTierUpgrade) return 'upgrade';
  if (isTierDowngrade) return 'downgrade';
  if (isBillingCycleUpgrade) return 'upgrade-billing-cycle-only';
  if (isBillingCycleDowngrade) return 'downgrade-billing-cycle-only';

  return 'upgrade';
};

const getCheckoutLang = (userLang: string) => {
  const PADDLE_SUPPORTED_LANGS = ['en', 'de', 'fr', 'sv', 'es'];

  const langPart = userLang.split('-')[0];
  if (langPart === 'ca') {
    return 'es';
  }

  if (PADDLE_SUPPORTED_LANGS.includes(langPart)) {
    return langPart;
  }

  return 'en';
};

const mapStateToProps = (
  state: { session: SessionState, organization: OrganizationState } & MarketingCratePaymentsState,
) => ({
  isLoading: state.payments.tiers.isLoading
    || state.payments.subscription.isLoading
    || state.organization.analytics.isLoading,
  failed: state.payments.tiers.failed
    || state.organization.analytics.failed,
  userLanguage: state.session.language,
  userId: state.session.userId,
  tiers: state.payments.tiers.details || [],
  currentTierId: state.payments.subscription.details?.price_id,
  hasScheduledAction: Boolean(state.payments.subscription.details?.scheduled_change_action),
  organizationId: state.organization.analytics.dataLayer.organization_id,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  billingService: BillingService
  history: History
  i18n: import('i18next').i18n
  organizationService: Organization
  config: ConfigService
  marketing: MarketingService
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

const ChangePlanOverlay: React.FunctionComponent<Props> = ({
  userLanguage, i18n, history, t, billingService,
  tiers, currentTierId, organizationId, isLoading,
  failed, organizationService, config, userId, hasScheduledAction, marketing,
}) => {
  const paddleEnvironment = config.get('PADDLE_ENVIRONMENT');
  const paddleToken = config.get('PADDLE_UI_TOKEN');
  const dataCenter = config.get('SENTRY_ENVIRONMENT');

  const [paddle, setPaddle] = useState<Paddle>();
  const [tierSelectorState, setTierSelectorState] = useState<Tier | undefined>();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState<ChangeProcessingModalVariant | null>(null);

  const fetchData = async () => {
    try {
      await Promise.all([
        billingService.getTiers(),
        organizationService.fetchProductAnalytics(),
        billingService.getCurrentSubscriptionTier(),
      ]);
      // eslint-disable-next-line no-empty
    } catch { }
  };

  if (!tierSelectorState && tiers.length > 0 && !isLoading) {
    if (currentTierId) {
      setTierSelectorState(
        findTierById(tiers, currentTierId),
      );
    } else {
      const findEmptyStateTier = () => tiers.find(
        tier => tier.billing_cycle === EMPTY_STATE_TIER.billing_cycle
            && tier.traffic === EMPTY_STATE_TIER.traffic,
      );

      setTierSelectorState(() => findEmptyStateTier());
    }
  }

  const handlePaddleEvents = async (event: PaddleEventData) => {
    if (event.name === 'checkout.completed') {
      window.Paddle?.Checkout.close();
      setShowInfoModal('subscription_created');
    }
  };


  useEffect(() => {
    fetchData();

    initializePaddle({
      environment: paddleEnvironment as Environments,
      token: paddleToken,
      checkout: {
        settings: {
          locale: getCheckoutLang(userLanguage),
        },
      },
      eventCallback: handlePaddleEvents,
    }).then(
      (paddleInstance: Paddle | undefined) => {
        if (paddleInstance) {
          setPaddle(paddleInstance);
        }
      },
    );
  }, []);


  const updatePlan = async (planId) => {
    const action = getAction(
      tierSelectorState, findTierById(tiers, currentTierId),
    );
    try {
      setPreviewLoading(true);
      await billingService.previewNewSubscription(planId).then(
        async (response) => {
          let creditsIssued = false;
          const {
            immediate_transaction_price,
            immediate_transaction_tax,
            next_billed_at,
            recurring_transaction_price,
            recurring_transaction_tax,
            currency,
          } = response;

          let immediateTransactionPrice = (
            parseInt(immediate_transaction_price, 10) + parseInt(immediate_transaction_tax, 10)
          ) / 100;

          const recurringTransactionPrice = (
            parseInt(recurring_transaction_price, 10) + parseInt(recurring_transaction_tax, 10)
          ) / 100;

          if (immediateTransactionPrice < 0) {
            creditsIssued = true;
            immediateTransactionPrice = Math.abs(immediateTransactionPrice);
          }

          const formattedImmediateTransactionPrice = i18n.format(
            new Currency(
              immediateTransactionPrice,
              currency,
            ),
            userLanguage,
          );

          const recurringImmediateTransactionPrice = i18n.format(
            new Currency(
              recurringTransactionPrice,
              currency,
            ),
            userLanguage,
          );

          await billingService.applyNewSubscription({
            onConfirmClick: () => {
              if (action) setShowInfoModal('payment_processing');
            },
            priceId: planId,
            creditsIssued,
            billingCycle: tierSelectorState?.billing_cycle,
            action,
            immediateTransactionPrice: formattedImmediateTransactionPrice,
            recurringTransactionPrice: recurringImmediateTransactionPrice,
            nextBillingPeriod: formatDate(
              moment(next_billed_at),
              i18n.language.split('-')[0],
              formatToOptions('LL'),
            ),
          }).then(
            () => {
              setShowInfoModal(['upgrade', 'upgrade-billing-cycle-only'].includes(action) ? 'subscription_updated' : 'change_scheduled');
            },
          ).catch((err) => {
            if (err.name !== 'ConfirmationRejectError') {
              setShowInfoModal('payment_failed');
            }
          });
        },
      ).catch();
      // eslint-disable-next-line no-empty
    } catch { } finally {
      setPreviewLoading(false);
    }
  };

  const openPaddlesCheckout = () => {
    if (!tierSelectorState || !dataCenter) return;

    paddle?.Checkout.open({
      items: [{ priceId: tierSelectorState.id, quantity: 1 }],
      customData: {
        organization_id: organizationId,
        data_center: dataCenter.split('.')[0],
        user_id: userId,
      },
    });
  };

  const onContinueWithNewTierClick = () => {
    if (!currentTierId) {
      openPaddlesCheckout();
      return;
    }

    if (!tierSelectorState) {
      return;
    }

    updatePlan(tierSelectorState.id);
  };

  const onMonthlyActionsLimitSelect = (value) => {
    setTierSelectorState(findTierById(tiers, value));
  };

  const onTogglerChange = (period) => {
    const newTier = tiers.find(
      tier => tier.billing_cycle === period && tier.traffic === tierSelectorState?.traffic,
    );
    setTierSelectorState(() => findTierById(tiers, newTier?.id));
  };

  const getTotalSave = () => {
    const traffic = tierSelectorState?.traffic;
    const monthlyPrice = tiers?.find(tier => tier.billing_cycle === 'month' && tier.traffic === traffic);
    const yearlyPrice = tiers?.find(tier => tier.billing_cycle === 'year' && tier.traffic === traffic);
    if (!monthlyPrice?.price || !yearlyPrice?.price) return '';

    return (monthlyPrice?.price * 12 - yearlyPrice?.price) / 100;
  };


  const filteredTiers = tiers?.filter(
    plan => plan.billing_cycle === (findTierById(tiers, tierSelectorState?.id)?.billing_cycle || 'year'),
  ).sort(
    (a, b) => a.traffic - b.traffic,
  ).map(
    ({ id, traffic }) => ({
      id,
      name: i18n.format(Number(traffic), '', userLanguage),
      value: id,
    }),
  ) || [];

  const tiersElements = filteredTiers.map(tier => ({
    ...tier,
    name: (
      <Stack spacing="narrow" verticalAlignment="top">
        <Stack.Item>
          {tier.name}
        </Stack.Item>
        {tier.id === currentTierId && (
          <Stack.Item fill>
            <Badge
              display="block"
              color="white"
              text={t('marketing.change-plan-overlay.monthly-action-limit-select.current-plan-badge')}
              size="small"
            />
          </Stack.Item>
        )}
      </Stack>
    ),
  }));

  const wrapWithStatus = (children: React.ReactElement): React.ReactElement => {
    if (failed) {
      return (
        <Stack
          vertical
          horizontalAlignment="middle"
          verticalAlignment="center"
          spacing="extraWide"
        >
          <Stack.Item fill />
          <Stack.Item previousSpacing="narrow">
            <Stack
              vertical
              horizontalAlignment="middle"
            >
              <Stack.Item>
                <Icon
                  color="red"
                  name="exclamation-mark-circle"
                  size="medium"
                />
              </Stack.Item>
              <Stack.Item>
                <TextStyle type="negative">
                  {t('general.cardWithControls.failMessage.description')}
                </TextStyle>
              </Stack.Item>
            </Stack>
          </Stack.Item>
          <Stack.Item fill />
        </Stack>
      );
    }
    if (isLoading) {
      return (
        <Stack vertical horizontalAlignment="middle">
          <Stack.Item fill previousSpacing="extraWide">
            <Skeleton roundBorder width="100%" height={40} />
          </Stack.Item>
          <Stack.Item>
            <Skeleton roundBorder width={300} height={22} />
          </Stack.Item>
        </Stack>
      );
    }

    return children;
  };

  const isCurrentPlanSelected = tierSelectorState?.id === currentTierId;
  const getDisabledButtonTooltip = () => {
    if (hasScheduledAction) return t('marketing.change-plan-overlay.core-card.continue-button-pending-change-tooltip');
    if (isCurrentPlanSelected) return t('marketing.change-plan-overlay.core-card.continue-button-current-plan-tooltip');
    return '';
  };

  return (
    <>
      {
        showInfoModal && (
          <ChangeProcessingInfoModal
            variant={showInfoModal}
            onClose={() => {
              fetchData();
              setShowInfoModal(null);
              history.goBack();
            }}
          />
        )
      }
      <OverlayView
        isOpen
        onClose={() => (history.length > 1 ? history.goBack() : history.push('/'))}
        title={t('marketing.change-plan-overlay.overlay.title')}
      >
        <View>
          <View.Body>
            <Layout maxWidth="medium">
              <Stack
                vertical
                fullHeight
                verticalAlignment="center"
                horizontalAlignment="middle"
                spacing="extraWide"
              >
                <Stack.Item previousSpacing="default" />
                <Stack.Item />
                <Stack.Item>
                  <Header>
                    {t('marketing.change-plan-overlay.plan-details.header')}
                  </Header>
                </Stack.Item>
                <Stack.Item previousSpacing="narrow">
                  <Text type="subdued">
                    {t('marketing.change-plan-overlay.plan-details.subheader')}
                  </Text>
                </Stack.Item>
                <Stack.Item previousSpacing="narrow" />
                <div className={bindClasses('pricing-plan-card-container')}>
                  <div className={bindClasses('pricing-plan-card')}>
                    <StyledContainer fullHeight border={['left', 'right', 'top', 'bottom']} backgroundColor="white" padding="extraWide">
                      <div className={bindClasses('pricing-plan-inner-container')}>
                        <Stack vertical>
                          <Stack.Item>
                            <PlanCard
                              t={t}
                              onPeriodChange={value => onTogglerChange(value)}
                              period={tierSelectorState?.billing_cycle}
                              header={t('marketing.change-plan-overlay.core-card.title')}
                              totalSave={getTotalSave().toString()}
                              priceHeader={
                                tierSelectorState?.price && (
                                  <div className={bindClasses('base-price')}>
                                    {EURO_SIGN}
                                    {i18n.format(Number(tierSelectorState?.price / 100), '', userLanguage)}
                                  </div>
                                )
                              }
                              subheader={t('marketing.change-plan-overlay.core-card.subtitle')}
                            >
                              {wrapWithStatus(
                                <Stack vertical horizontalAlignment="middle">
                                  <Stack.Item fill>
                                    <Select
                                      label={t('marketing.change-plan-overlay.monthly-action-limit-select.label')}
                                      name="monthlyActionsLimit"
                                      value={tierSelectorState?.id}
                                      onChange={
                                        (e: { target: { value: string } }) =>
                                          onMonthlyActionsLimitSelect(e.target.value)
                                      }
                                      options={[
                                        {
                                          id: '',
                                          name: '',
                                          items: tiersElements || [],
                                        },
                                      ]}
                                    />
                                  </Stack.Item>
                                  <Stack.Item fill>
                                    <Tooltip position="bottom" content={getDisabledButtonTooltip()}>
                                      <Button
                                        disabled={hasScheduledAction || isCurrentPlanSelected}
                                        pending={previewLoading}
                                        fullWidth
                                        text={t('marketing.change-plan-overlay.core-card.continue-button')}
                                        onClick={() => onContinueWithNewTierClick()}
                                        appearance="primary"
                                      />
                                    </Tooltip>
                                  </Stack.Item>
                                </Stack>,
                              )}
                            </PlanCard>
                          </Stack.Item>
                          <Stack.Item previousSpacing="extraWide">
                            <FeatureList variant="core" />
                          </Stack.Item>
                        </Stack>
                      </div>
                    </StyledContainer>
                  </div>
                  <div className={bindClasses('pricing-plan-card')}>
                    <StyledContainer fullHeight border={['left', 'right', 'top', 'bottom']} backgroundColor="white" padding="extraWide">
                      <div className={bindClasses('pricing-plan-inner-container')}>
                        <Stack vertical>
                          <Stack.Item>
                            <PlanCard
                              t={t}
                              header={t('marketing.change-plan-overlay.enterprise-card.title')}
                              priceHeader={(
                                <Stack verticalAlignment="center" spacing="narrow">
                                  <Stack.Item>
                                    <Text type="subdued">
                                      <div className={bindClasses('from-label-text')}>
                                        {t('marketing.change-plan-overlay.enterprise-card.from-label')}
                                      </div>
                                    </Text>
                                  </Stack.Item>
                                  <Stack.Item>
                                    <div className={bindClasses('base-price')}>
                                      {EURO_SIGN}
                                      {i18n.format(Number(10995), '', userLanguage)}
                                      {t('marketing.change-plan-overlay.enterprise-card.yearly-label')}
                                    </div>
                                  </Stack.Item>
                                </Stack>
                              )}
                              subheader={t('marketing.change-plan-overlay.enterprise-card.subtitle')}
                            >
                              <Button
                                fullWidth
                                text={t('marketing.change-plan-overlay.enterprise-card.contact-sales-button')}
                                onClick={() => marketing.openProductContactPage({ topic: 'I need higher data limit' })}
                                appearance="primary"
                              />
                            </PlanCard>
                          </Stack.Item>
                          <Stack.Item previousSpacing="extraWide">
                            <FeatureList variant="enterprise" />
                          </Stack.Item>
                        </Stack>
                      </div>
                    </StyledContainer>
                  </div>
                </div>
                <Stack.Item />
                <Stack.Item previousSpacing="narrow">
                  <HelpLink href={marketing.getPiwikProductPricingTablePageURL()}>
                    Piwik PRO plans comparison
                  </HelpLink>
                </Stack.Item>
              </Stack>
            </Layout>
          </View.Body>
        </View>
      </OverlayView>
    </>
  );
};

export default connector(withInject<InjectedProps>({
  billingService: 'MarketingCrate.billingService',
  organizationService: 'OrganizationCrate.organization',
  i18n: 'TranslationCrate.i18n',
  history: 'RouterCrate.history',
  config: 'config',
  marketing: 'MarketingCrate.marketingService',
})(
  withTranslation('administration')(errorBoundary(ChangePlanOverlay)),
));
